import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Scale, Download, RefreshCw, TrendingUp, TrendingDown, Wallet, Info } from 'lucide-react';

const BalanceGeneralPage = () => {
  const { empresaActual } = useApp();
  const [loading, setLoading] = useState(true);
  const [fechaCorte, setFechaCorte] = useState(new Date().toISOString().split('T')[0]);
  const [balance, setBalance] = useState({
    activos: [],
    pasivos: [],
    patrimonio: [],
    totales: { activos: 0, pasivos: 0, patrimonio: 0 },
    cuadrado: true,
    detalle_calculo: {}
  });

  const loadBalance = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/reportes/balance-general?empresa_id=${empresaActual.id}&fecha_corte=${fechaCorte}`);
      setBalance(res.data);
    } catch (error) {
      console.error('Error loading balance:', error);
      toast.error('Error al cargar balance');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, fechaCorte]);

  useEffect(() => { loadBalance(); }, [loadBalance]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-PE', { 
      style: 'currency', 
      currency: 'PEN',
      minimumFractionDigits: 2 
    }).format(amount || 0);
  };

  const renderCuentaRow = (cuenta) => {
    const isHeader = cuenta.codigo?.split('.').length <= 2;
    return (
      <div 
        key={cuenta.codigo} 
        className={`flex justify-between py-2 px-3 ${isHeader ? 'bg-gray-50 font-semibold' : ''}`}
      >
        <span className={isHeader ? 'text-gray-900' : 'text-gray-600'}>
          {cuenta.codigo} - {cuenta.nombre}
        </span>
        <span className={`font-mono ${cuenta.saldo > 0 ? 'text-gray-900' : cuenta.saldo < 0 ? 'text-red-600' : 'text-gray-400'}`}>
          {cuenta.saldo !== 0 ? formatMoney(cuenta.saldo) : '-'}
        </span>
      </div>
    );
  };

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  return (
    <div className="space-y-6" data-testid="balance-general-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Balance General</h1>
            <p className="text-sm text-gray-500">Estado de situación financiera</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Fecha de corte:</Label>
            <Input 
              type="date" 
              value={fechaCorte} 
              onChange={(e) => setFechaCorte(e.target.value)}
              className="w-40"
            />
          </div>
          <Button variant="outline" onClick={loadBalance} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Actualizar
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />Exportar
          </Button>
        </div>
      </div>

      {/* Resumen Cards */}
      <div className="grid grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Activos</p>
                <p className="text-3xl font-bold text-blue-700">{formatMoney(balance.totales.activos)}</p>
              </div>
              <TrendingUp className="w-12 h-12 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Total Pasivos</p>
                <p className="text-3xl font-bold text-red-700">{formatMoney(balance.totales.pasivos)}</p>
              </div>
              <TrendingDown className="w-12 h-12 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Patrimonio Neto</p>
                <p className="text-3xl font-bold text-purple-700">{formatMoney(balance.totales.patrimonio)}</p>
              </div>
              <Wallet className="w-12 h-12 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* ACTIVOS */}
          <Card>
            <CardHeader className="bg-blue-50 border-b">
              <CardTitle className="flex items-center justify-between text-blue-800">
                <span>ACTIVOS</span>
                <span className="text-xl">{formatMoney(balance.totales.activos)}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 divide-y">
              {balance.activos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No hay cuentas de activo</div>
              ) : (
                balance.activos.map(cuenta => renderCuentaRow(cuenta))
              )}
              <div className="flex justify-between py-3 px-3 bg-blue-100 font-bold text-blue-900">
                <span>TOTAL ACTIVOS</span>
                <span className="font-mono">{formatMoney(balance.totales.activos)}</span>
              </div>
            </CardContent>
          </Card>

          {/* PASIVOS Y PATRIMONIO */}
          <div className="space-y-6">
            {/* Pasivos */}
            <Card>
              <CardHeader className="bg-red-50 border-b">
                <CardTitle className="flex items-center justify-between text-red-800">
                  <span>PASIVOS</span>
                  <span className="text-xl">{formatMoney(balance.totales.pasivos)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y">
                {balance.pasivos.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No hay cuentas de pasivo</div>
                ) : (
                  balance.pasivos.map(cuenta => renderCuentaRow(cuenta))
                )}
                <div className="flex justify-between py-3 px-3 bg-red-100 font-bold text-red-900">
                  <span>TOTAL PASIVOS</span>
                  <span className="font-mono">{formatMoney(balance.totales.pasivos)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Patrimonio */}
            <Card>
              <CardHeader className="bg-purple-50 border-b">
                <CardTitle className="flex items-center justify-between text-purple-800">
                  <span>PATRIMONIO</span>
                  <span className="text-xl">{formatMoney(balance.totales.patrimonio)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y">
                {balance.patrimonio.map(cuenta => renderCuentaRow(cuenta))}
                <div className="flex justify-between py-3 px-3 bg-purple-100 font-bold text-purple-900">
                  <span>TOTAL PATRIMONIO</span>
                  <span className="font-mono">{formatMoney(balance.totales.patrimonio)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Total Pasivo + Patrimonio */}
            <Card className="bg-gray-800 text-white">
              <CardContent className="py-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">PASIVO + PATRIMONIO</span>
                  <span className="text-2xl font-bold font-mono">
                    {formatMoney(balance.totales.pasivos + balance.totales.patrimonio)}
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {balance.cuadrado ? '✓ Balance cuadrado' : '⚠ Descuadre detectado'}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Detalle de cálculo */}
      {balance.detalle_calculo && Object.keys(balance.detalle_calculo).length > 0 && (
        <Card className="bg-slate-50 border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
              <Info className="w-4 h-4" />
              Detalle del cálculo (datos reales)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4 text-sm">
              <div className="bg-white p-3 rounded border">
                <p className="text-gray-500 text-xs">Bancos/Caja</p>
                <p className="font-semibold text-blue-600">{formatMoney(balance.detalle_calculo.saldo_bancos)}</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <p className="text-gray-500 text-xs">Cuentas x Cobrar</p>
                <p className="font-semibold text-green-600">{formatMoney(balance.detalle_calculo.saldo_cxc)}</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <p className="text-gray-500 text-xs">Inventario</p>
                <p className="font-semibold text-amber-600">{formatMoney(balance.detalle_calculo.saldo_inventario)}</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <p className="text-gray-500 text-xs">Cuentas x Pagar</p>
                <p className="font-semibold text-red-600">{formatMoney(balance.detalle_calculo.saldo_cxp)}</p>
              </div>
              <div className="bg-white p-3 rounded border">
                <p className="text-gray-500 text-xs">IGV por Pagar</p>
                <p className="font-semibold text-orange-600">{formatMoney(balance.detalle_calculo.saldo_igv)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Nota informativa */}
      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="py-4">
          <p className="text-sm text-emerald-800">
            <strong>✓ Datos Reales:</strong> Este balance calcula los saldos automáticamente desde: 
            movimientos bancarios, ventas a crédito pendientes, compras pendientes de pago, 
            y los saldos iniciales configurados en cada cuenta contable.
            Para ajustar saldos iniciales, ve a <strong>Catálogos → Cuentas Contables</strong>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BalanceGeneralPage;
